import { Button, Dialog } from "@rio-cloud/rio-uikit";
import { useEffect, useState, useRef } from "react";
import { debounce } from "lodash";

interface ISearchAutocompleteProps {
  id: string;
  placeholder: string;
  fetchOptions: (searchTerm: string) => Promise<any[]>;
  optionLabel: { [key: string]: string };
  onSelectedOption: (e: any) => void;
  validateOption?: (option: any) => Promise<boolean>;
  overlay?: boolean;
  overlayClassName?: string;
  disabled?: boolean;
  optionRenderer?: (option: any) => React.ReactNode;
  valueRenderer?: (option: any) => string;
  defaultValue?: any;
  onClear?: () => void;
  allowManualAdd?: boolean;
  clearAfterSelect?: boolean;
  debounceDelay?: number;
}

const SearchAutocomplete = (props: ISearchAutocompleteProps) => {
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [options, setOptions] = useState<any[]>([]);
  const [optionSelected, setOptionSelected] = useState<any | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const [showAddValueDialog, setShowAddValueDialog] = useState(false);
  const [addValue, setAddValue] = useState<any | null>(null);
  
  const searchContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (props.defaultValue) {
      setOptionSelected(props.defaultValue);
    }
  }, [props.defaultValue]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const debounceDelay = props.debounceDelay ?? 0;
  const useDebounce = debounceDelay > 0;

  const debouncedFetch = useRef(
    useDebounce
      ? debounce(async (term: string) => {
          if (!term?.trim()) {
            setOptions([]);
            setLoading(false);
            return;
          }

          setLoading(true);
          try {
            const response = await props.fetchOptions(term);
            setOptions(response);
          } catch (error) {
            console.error('Erro ao buscar opções:', error);
            setOptions([]);
          } finally {
            setLoading(false);
          }
        }, debounceDelay)
      : null
  ).current;

  useEffect(() => {
    if (!searchTerm?.trim()) {
      setOptions([]);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await props.fetchOptions(searchTerm);
        setOptions(response);
      } catch (error) {
        console.error('Erro ao buscar opções:', error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    };

    if (useDebounce && debouncedFetch) {
      debouncedFetch(searchTerm);
      return () => {
        debouncedFetch.cancel();
      };
    } else {
      fetchData();
    }
  }, [searchTerm, props.fetchOptions, useDebounce, debouncedFetch]);

  const loadingElement = () => {
    return (
      <div className=" w-full">
        <div className="spinnerInfoBox display-flex justify-content-center align-items-center height-100pct">
          <div className="spinner">
            <div></div>
            <div></div>
            <div></div>
          </div>
          <div className="spinnerText">Buscando...</div>
        </div>
      </div>
    );
  };

  return (
    <>
      {optionSelected ? (
        <div className="relative" ref={searchContainerRef}>
          <input
            id={`${props.id}-selected`}
            type="text"
            className="form-control"
            placeholder={props.placeholder}
            value={
              props.valueRenderer
                ? props.valueRenderer(optionSelected)
                :
                Object.entries(props.optionLabel)
                  .map(
                    ([key, value]) => optionSelected[key as keyof typeof optionSelected] ? `${value}: ${optionSelected[key as keyof typeof optionSelected]}` : null
                  )
                  .filter(Boolean)
                  .join(" - ")}
            onChange={(e) => {
              if (e.target.value === '') {
                setOptionSelected(null);
                setSearchTerm("");
                setIsDropdownOpen(false);
                props.onClear?.();
              }
            }}
            disabled={props.disabled}
          />
        </div>
      ) : (
        <div className="relative" ref={searchContainerRef}>
          <input
            id={props.id}
            type="text"
            className="form-control"
            placeholder={props.placeholder}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              if (e.target.value === '') {
                setIsDropdownOpen(false);
                props.onClear?.();
              } else {
                setIsDropdownOpen(true);
              }
            }}
            onFocus={() => searchTerm && setIsDropdownOpen(true)}
            disabled={props.disabled}
          />
          {searchTerm && isDropdownOpen && (
            <div
              className={`bg-white rounded shadow p-2 mt-2 z-10 w-full max-h-80 overflow-y-auto ${props.overlay
                  ? `absolute top-100 ${props.overlayClassName}`
                  : ""
                }`}
            >
              {loading && loadingElement()}
              {!loading &&
                options.map((optionEl, index) => (
                  <div
                    key={index}
                    className="p-2 mb-2 cursor-pointer"
                    onClick={async () => {
                      if (props.validateOption) {
                        const isValid = await props.validateOption(optionEl);
                        if (!isValid) return;
                      }

                      if (props.clearAfterSelect) {
                        setSearchTerm("");
                        setIsDropdownOpen(false);
                      } else {
                        setOptionSelected(optionEl);
                        setSearchTerm("");
                        setIsDropdownOpen(false);
                      }
                      props.onSelectedOption(optionEl);
                    }}
                  >
                    {props.optionRenderer
                      ? props.optionRenderer(optionEl)
                      : Object.entries(props.optionLabel)
                        .map(
                          ([key, value]) => optionEl[key as keyof typeof optionEl] ? `${value}: ${optionEl[key as keyof typeof optionEl]}` : null
                        )
                        .filter(Boolean)
                        .join(" - ")}
                  </div>
                ))}

              {!loading && options.length === 0 && (
                <div className="p-2 mb-2 cursor-block">
                  Nenhum resultado encontrado
                </div>
              )}

              {!loading && props.allowManualAdd && <div
                className="p-2 mb-2 cursor-pointer text-color-primary"
                onClick={() => {
                  setShowAddValueDialog(true);
                }}
              >
                Adicionar valor manualmente
              </div>}
            </div>
          )}
        </div>
      )}

      <Dialog
        show={showAddValueDialog}
        onClose={() => setShowAddValueDialog(false)}
        title="Adicionar valor"
        bsSize="sm"
        showCloseButton
        footer={
          <div>
            <Button
              bsStyle="primary"
              disabled={
                !props.optionLabel ||
                !Object.keys(props.optionLabel).every(key => addValue?.[key]?.trim())
              }
              onClick={() => {
                const trimmedValue = Object.fromEntries(
                  Object.entries(addValue).map(([k, v]) => [k, typeof v === "string" ? v.trim() : v])
                );
                props.onSelectedOption(trimmedValue);
                setAddValue("");
                setShowAddValueDialog(false);
              }}
            >Adicionar</Button>
          </div>
        }
        body={<div>
          <div className="form-group">
            {
              props.optionLabel && Object.entries(props.optionLabel).map(([key, value]) => (
                <div className="form-group">
                  <label htmlFor="addValue">{value}</label>
                  <input
                    className="form-control"
                    placeholder={value}
                    value={addValue?.[key]}
                    onChange={(e) => setAddValue({ ...addValue, [key]: e.target.value })}
                  />
                </div>
              ))}
          </div>
        </div>}
      />
    </>
  );
};

export default SearchAutocomplete;
